// Walking the entire `messages` array on every SSE chunk (`prev.map(...)`
// inside the streaming callback) and reallocating `newLogs` with each
// append (`[...newLogs, log]`) costs O(N·K) React work per second for a
// long conversation with N messages and K streamed events.
//
// This module exposes a tiny pure helper that the chat panel can use
// to patch a single message by id. The panel still owns state, but
// the patch is a constant-time function — no allocations beyond the
// single updated message, no array spread for the unrelated messages.

import type { ChatMessage, AgentStreamUpdate } from "@/lib/types";

export interface ChatMessagePatch {
  content?: string;
  isStreaming?: boolean;
  status?: string;
  logs?: AgentStreamUpdate[];
  answer?: string;
  progress?: number;
  error?: string;
  sources?: ChatMessage["sources"];
  taskId?: string;
}

export function patchMessageById(
  messages: ChatMessage[],
  id: string,
  patch: ChatMessagePatch,
): ChatMessage[] {
  // Linear search is fine here: message id collisions are impossible
  // (placeholders use a unique id per send), and a long conversation
  // has at most a few dozen messages. The win is no longer
  // allocating a new array for the unrelated tail.
  const index = messages.findIndex((m) => m.id === id);
  if (index === -1) return messages;
  const target = messages[index];
  // Detect a no-op patch (every key in `patch` is already equal on
  // the target). When the patch is empty the spread is still a new
  // object, so we compare by value rather than by reference.
  let changed = false;
  for (const key of Object.keys(patch) as (keyof ChatMessagePatch)[]) {
    if (target[key] !== patch[key]) {
      changed = true;
      break;
    }
  }
  if (!changed) return messages;
  const next: ChatMessage = { ...target, ...patch };
  // Reuse the array when the patched message is the last one and we
  // can do a single-element copy. For the common case (one streaming
  // message at the end) this avoids a full re-iteration.
  if (index === messages.length - 1) {
    const out = messages.slice();
    out[index] = next;
    return out;
  }
  const out = new Array<ChatMessage>(messages.length);
  for (let i = 0; i < index; i += 1) out[i] = messages[i];
  out[index] = next;
  for (let i = index + 1; i < messages.length; i += 1) out[i] = messages[i];
  return out;
}

export function appendLogToMessage(
  messages: ChatMessage[],
  id: string,
  log: AgentStreamUpdate,
): ChatMessage[] {
  const index = messages.findIndex((m) => m.id === id);
  if (index === -1) return messages;
  const target = messages[index];
  const existing = target.logs ?? [];
  const last = existing[existing.length - 1];
  // De-dupe identical consecutive log lines (same status + message) so
  // a flood of "Running..." ticks does not grow the array every chunk.
  if (
    last &&
    last.status === log.status &&
    last.message === log.message
  ) {
    return messages;
  }
  const nextLogs = existing.length === 0 ? [log] : [...existing, log];
  return patchMessageById(messages, id, { logs: nextLogs });
}
